/**
 * Feature Codes Constants
 * 
 * This file contains all feature codes definitions with their translations
 * and metadata. This ensures consistency across the application and makes
 * it easy to manage feature codes centrally.
 * 
 * @author Rodrigo Cuadra
 * @company Ring2All LLC
 * @created 2025-01-12
 */

export interface FeatureCodeDefinition {
  id: string;
  name: string;
  defaultNumber: string;
  category: string;
  description?: string;
  enabled?: boolean;
}

export interface FeatureCodeCategory {
  id: string;
  name: string;
  description?: string;
  features: FeatureCodeDefinition[];
}

// Feature Code Categories
export const FEATURE_CODE_CATEGORIES: FeatureCodeCategory[] = [
  {
    id: 'blacklist',
    name: 'Blacklist',
    description: 'Call blocking and blacklist management features',
    features: [
      {
        id: 'add_number_to_blacklist',
        name: 'Add number to blacklist',
        defaultNumber: '*30',
        category: 'blacklist',
        description: 'Add a number to the blacklist'
      },
      {
        id: 'remove_number_from_blacklist',
        name: 'Remove number from blacklist',
        defaultNumber: '*31',
        category: 'blacklist',
        description: 'Remove a number from the blacklist'
      },
      {
        id: 'blacklist_the_last_caller',
        name: 'Blacklist the last caller',
        defaultNumber: '*32',
        category: 'blacklist',
        description: 'Automatically blacklist the last caller'
      }
    ]
  },
  {
    id: 'business_services',
    name: 'Business Services',
    description: 'Business productivity and administrative features',
    features: [
      {
        id: 'wakeup_call_self',
        name: 'Wakeup Call (self)',
        defaultNumber: '*34',
        category: 'business_services',
        description: 'Schedule a wakeup call to your own extension'
      },
      {
        id: 'remote_wakeup_call',
        name: 'Remote Wakeup Call',
        defaultNumber: '*35',
        category: 'business_services',
        description: 'Schedule a wakeup call for another extension'
      },
      {
        id: 'reminder_message_scheduler',
        name: 'Reminder Message Scheduler',
        defaultNumber: '*36',
        category: 'business_services',
        description: 'Record a message and schedule its playback later'
      },
      {
        id: 'speak_last_caller_number',
        name: 'Speak Last Caller Number',
        defaultNumber: '*37',
        category: 'business_services',
        description: 'Announces the number of the last inbound call'
      },
      {
        id: 'lock_unlock_phone',
        name: 'Lock/Unlock Phone',
        defaultNumber: '*75',
        category: 'business_services',
        description: 'Prevent outbound calls from this device'
      },
      {
        id: 'change_feature_password',
        name: 'Change Feature Password',
        defaultNumber: '*76',
        category: 'business_services',
        description: 'Change password used in features'
      },
      {
        id: 'remote_substitution',
        name: 'Remote Substitution',
        defaultNumber: '*77',
        category: 'business_services',
        description: 'Temporarily assume another extension\'s identity'
      },
      {
        id: 'enter_customer_code_for_cdr',
        name: 'Enter Customer Code for CDR',
        defaultNumber: '*78',
        category: 'business_services',
        description: 'Append billing/customer code for CDR tagging'
      },
      {
        id: 'authorization_code_for_cos_override',
        name: 'Authorization Code for COS override',
        defaultNumber: '*79',
        category: 'business_services',
        description: 'Override Class of Service temporarily using a code'
      },
      {
        id: 'hotdesking_login_logout',
        name: 'Hotdesking Login/Logout',
        defaultNumber: '*80',
        category: 'business_services',
        description: 'Login/logout from any phone, dynamically assigning extensions'
      },
      {
        id: 'paging',
        name: 'Paging',
        defaultNumber: '*82',
        category: 'business_services',
        description: 'One-way announcement to all members of a paging group'
      },
      {
        id: 'paging_duplex_mode',
        name: 'Paging Duplex Mode',
        defaultNumber: '*83',
        category: 'business_services',
        description: 'Two-way paging (walkie-talkie style)'
      },
      {
        id: 'toggle_anonymous_calling',
        name: 'Toggle Anonymous Calling',
        defaultNumber: '*84',
        category: 'business_services',
        description: 'Toggle caller ID anonymization per call'
      },
      {
        id: 'purge_cache_key_exact_match',
        name: 'Purge Cache Key (Exact Match)',
        defaultNumber: '*99',
        category: 'business_services',
        description: 'Remove a specific cache entry by key'
      }
    ]
  },
  {
    id: 'call_center_features',
    name: 'Call Center Features',
    description: 'Call center agent control and queue management features',
    features: [
      {
        id: 'queue_toggle',
        name: 'Queue Toggle',
        defaultNumber: '*50',
        category: 'call_center_features',
        description: 'Login or logout from a specific dynamic queue'
      },
      {
        id: 'queue_pause_toggle',
        name: 'Queue Pause Toggle',
        defaultNumber: '*51',
        category: 'call_center_features',
        description: 'Pause or unpause in a specific queue'
      },
      {
        id: 'global_queue_toggle',
        name: 'Global Queue Toggle',
        defaultNumber: '*52',
        category: 'call_center_features',
        description: 'Login or logout from all assigned dynamic queues'
      },
      {
        id: 'global_pause_toggle',
        name: 'Global Pause Toggle',
        defaultNumber: '*53',
        category: 'call_center_features',
        description: 'Pause or unpause across all queues simultaneously'
      },
      {
        id: 'hotdesk_queue_toggle',
        name: 'Hotdesk + Queue Toggle',
        defaultNumber: '*54',
        category: 'call_center_features',
        description: 'Hotdesking login followed by specific queue toggle'
      },
      {
        id: 'hotdesk_global_toggle',
        name: 'Hotdesk + Global Toggle',
        defaultNumber: '*55',
        category: 'call_center_features',
        description: 'Hotdesking login followed by global queue login/logout'
      }
    ]
  },
  {
    id: 'call_forwarding',
    name: 'Call Forwarding',
    description: 'Call forwarding and routing features',
    features: [
      {
        id: 'toggle_no_answer_forwarding',
        name: 'Toggle No Answer Forwarding',
        defaultNumber: '*64',
        category: 'call_forwarding',
        description: 'Enables/disables call forward when unanswered'
      },
      {
        id: 'set_no_answer_destination',
        name: 'Set No Answer Destination',
        defaultNumber: '*65',
        category: 'call_forwarding',
        description: 'Assigns the number to forward to on no answer'
      },
      {
        id: 'toggle_dnd_do_not_disturb',
        name: 'Toggle DND (Do Not Disturb)',
        defaultNumber: '*66',
        category: 'call_forwarding',
        description: 'Activates/deactivates DND mode'
      },
      {
        id: 'toggle_follow_me',
        name: 'Toggle Follow Me',
        defaultNumber: '*67',
        category: 'call_forwarding',
        description: 'Enables or disables follow-me behavior'
      },
      {
        id: 'clear_all_phone_settings',
        name: 'Clear All Phone Settings',
        defaultNumber: '*69',
        category: 'call_forwarding',
        description: 'Resets phone-specific forwarding, follow-me, and DND settings'
      },
      {
        id: 'toggle_all_call_flows',
        name: 'Toggle All Call Flows',
        defaultNumber: '*81',
        category: 'call_forwarding',
        description: 'Enables/disables all Call Flows globally for the tenant'
      },
      {
        id: 'toggle_time_condition',
        name: 'Toggle Time Condition',
        defaultNumber: '*82',
        category: 'call_forwarding',
        description: 'Enables/disables time-based routing override'
      }
    ]
  },
  {
    id: 'on_call_features',
    name: 'On Call Features',
    description: 'Features available during active calls',
    features: [
      {
        id: 'disconnect_call',
        name: 'Disconnect Call',
        defaultNumber: '*0',
        category: 'on_call_features',
        description: 'Immediately hangs up the current call'
      },
      {
        id: 'pause_resume_outbound_recording',
        name: 'Pause/Resume Outbound Recording',
        defaultNumber: '*00',
        category: 'on_call_features',
        description: 'Toggles outbound recording via Lua'
      },
      {
        id: 'direct_pickup',
        name: 'Direct Pickup',
        defaultNumber: '*07',
        category: 'on_call_features',
        description: 'Answer another ringing extension'
      },
      {
        id: 'group_pickup',
        name: 'Group Pickup',
        defaultNumber: '*08',
        category: 'on_call_features',
        description: 'Pickup from within the same pickup group'
      },
      {
        id: 'pause_resume_inbound_recording',
        name: 'Pause/Resume Inbound Recording',
        defaultNumber: '*1',
        category: 'on_call_features',
        description: 'Toggles inbound recording via Lua'
      },
      {
        id: 'attended_transfer',
        name: 'Attended Transfer',
        defaultNumber: '*2',
        category: 'on_call_features',
        description: 'Initiates attended transfer'
      },
      {
        id: 'one_touch_recording',
        name: 'One-Touch Recording',
        defaultNumber: '*3',
        category: 'on_call_features',
        description: 'Begins a new call recording session'
      },
      {
        id: 'park_call',
        name: 'Park Call',
        defaultNumber: '*4',
        category: 'on_call_features',
        description: 'Transfers call to a parking lot'
      },
      {
        id: 'blind_transfer',
        name: 'Blind Transfer',
        defaultNumber: '#1',
        category: 'on_call_features',
        description: 'Performs blind transfer to ** context'
      }
    ]
  },
  {
    id: 'recordings_&_announcements',
    name: 'Recordings & Announcements',
    description: 'Recording and announcement management features',
    features: [
      {
        id: 'custom_recording',
        name: 'Custom Recording',
        defaultNumber: '*92',
        category: 'recordings_&_announcements',
        description: 'Records a voice message and saves it as a custom system announcement'
      },
      {
        id: 'direct_voicemail_access',
        name: 'Direct Voicemail Access',
        defaultNumber: '*97',
        category: 'recordings_&_announcements',
        description: 'Accesses the caller\'s own voicemail box using their extension'
      },
      {
        id: 'remote_voicemail_access',
        name: 'Remote Voicemail Access',
        defaultNumber: '*98',
        category: 'recordings_&_announcements',
        description: 'Prompts for mailbox number to access another voicemail box'
      }
    ]
  },
  {
    id: 'spy_modes',
    name: 'Spy Modes',
    description: 'Call monitoring and supervision features',
    features: [
      {
        id: 'spy_barge_mode',
        name: 'Spy (Barge Mode)',
        defaultNumber: '*85',
        category: 'spy_modes',
        description: 'Listen and talk in an ongoing call (active barge-in)'
      },
      {
        id: 'spy_normal',
        name: 'Spy (Normal)',
        defaultNumber: '*86',
        category: 'spy_modes',
        description: 'Passive call monitoring without audio injection'
      },
      {
        id: 'spy_whisper',
        name: 'Spy (Whisper)',
        defaultNumber: '*87',
        category: 'spy_modes',
        description: 'Talk to the agent only, without the caller hearing'
      },
      {
        id: 'spy_random',
        name: 'Spy (Random)',
        defaultNumber: '*88',
        category: 'spy_modes',
        description: 'Join a random active call channel for quality assurance'
      }
    ]
  },
  {
    id: 'test_services',
    name: 'Test Services',
    description: 'System testing and diagnostic features',
    features: [
      {
        id: 'speak_date_and_time',
        name: 'Speak Date and Time',
        defaultNumber: '*70',
        category: 'test_services',
        description: 'Announces the current server date and time'
      },
      {
        id: 'speak_your_extension',
        name: 'Speak Your Extension',
        defaultNumber: '*71',
        category: 'test_services',
        description: 'Speaks back the caller\'s extension'
      },
      {
        id: 'echo_test',
        name: 'Echo Test',
        defaultNumber: '*72',
        category: 'test_services',
        description: 'Repeats caller\'s audio in real time for loopback verification'
      },
      {
        id: 'simulate_incoming_call',
        name: 'Simulate Incoming Call',
        defaultNumber: '*73',
        category: 'test_services',
        description: 'Triggers an artificial inbound call simulation'
      },
      {
        id: 'test_music_on_hold',
        name: 'Test Music On Hold',
        defaultNumber: '*74',
        category: 'test_services',
        description: 'Plays the currently configured hold music'
      }
    ]
  }
];

// Helper functions
export const getFeatureCodeById = (id: string): FeatureCodeDefinition | undefined => {
  for (const category of FEATURE_CODE_CATEGORIES) {
    const feature = category.features.find(f => f.id === id);
    if (feature) return feature;
  }
  return undefined;
};

export const getFeatureCodeByNumber = (number: string): FeatureCodeDefinition | undefined => {
  for (const category of FEATURE_CODE_CATEGORIES) {
    const feature = category.features.find(f => f.defaultNumber === number);
    if (feature) return feature;
  }
  return undefined;
};

export const getCategoryById = (id: string): FeatureCodeCategory | undefined => {
  return FEATURE_CODE_CATEGORIES.find(c => c.id === id);
};

export const getAllFeatureCodes = (): FeatureCodeDefinition[] => {
  return FEATURE_CODE_CATEGORIES.flatMap(category => category.features);
};

// Translation key generators
export const getFeatureCodeTranslationKey = (feature: FeatureCodeDefinition): string => {
  const categoryKey = feature.category.toLowerCase().replace(/\s+/g, '_').replace(/&/g, '_').replace(/[^a-z0-9_]/g, '_');
  const featureKey = feature.id.toLowerCase().replace(/\s+/g, '_').replace(/[()]/g, '_').replace(/[^a-z0-9_]/g, '_');
  return `featureCodes.${categoryKey}.${featureKey}`;
};

export const getCategoryTranslationKey = (category: FeatureCodeCategory): string => {
  const categoryKey = category.id.toLowerCase().replace(/\s+/g, '_').replace(/&/g, '_').replace(/[^a-z0-9_]/g, '_');
  return `featureCodes.categories.${categoryKey}`;
};
